import { OrderDirection } from "@/admin/content-library/__generated__/AdminContentLibraryListTablePaginationQuery.graphql"
import { useActiveProduct } from "@/core/context/ActiveProductContext"
import { useLabel } from "@/core/context/LabelsContext"
import makeUseStyles from "@/core/ui/style/util/makeUseStyles"
import ProductMemberCard from "@/product/member/common/card/ProductMemberCard"
import ProductMembersListFloatingBar from "@/product/member/common/list/floating-bar/ProductMembersListFloatingBar"
import MembersListNoneFound from "@/product/member/common/list/none-found/MembersListNoneFound"
import ProductMembersListSkeleton from "@/product/member/common/list/skeleton/ProductMembersListSkeleton"
import { ProductMembersListPaginationQuery } from "@/product/member/common/list/__generated__/ProductMembersListPaginationQuery.graphql"
import { ProductMembersListQuery } from "@/product/member/common/list/__generated__/ProductMembersListQuery.graphql"
import { ProductMembersList_PaginationFragment$key } from "@/product/member/common/list/__generated__/ProductMembersList_PaginationFragment.graphql"
import {
  mapOrderByFieldToColumn,
  mapSelectedGroupFilterToInput,
  mapSelectedProfileFieldsToInput,
} from "@/product/member/page/ProductMembersListPageToolbar"
import { ProductMembersListToolbarState } from "@/product/member/page/useProductMembersListToolbarState"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { DiscoAlert, DiscoCheckbox, DiscoText, DiscoTextButton } from "@disco-ui"
import DiscoGrid from "@disco-ui/layout/DiscoGrid"
import { Theme } from "@material-ui/core"
import { useEffect, useState } from "react"
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useSubscribeToInvalidationState,
} from "react-relay"
import { graphql } from "relay-runtime"
import { ProductMembersList__membersDirectoryFiltersQuery } from "./__generated__/ProductMembersList__membersDirectoryFiltersQuery.graphql"

// These values should match up with the values in the ProductMembershipsOrderByField enum in our services
export type ProductMembersListOrderByField =
  | "creation_datetime"
  | "type"
  | "role"
  | "name"
  | "curriculum"
  | "attendance"
  | "comments"
  | "last_activity_at"
  | "groups"

const MEMBERS_PER_PAGE = 12
interface ProductMembersListProps {
  toolbarState: ProductMembersListToolbarState
  hideSelectAll?: boolean
  groovyDepths?: keyof Theme["palette"]["groovyDepths"]
  appId?: GlobalID
}

function ProductMembersList({
  toolbarState,
  hideSelectAll,
  groovyDepths,
  appId,
}: ProductMembersListProps) {
  /** an array of product membership id's with memberId */
  const [selectedMemberships, setSelectedMemberships] = useState<
    { id: GlobalID; memberId: GlobalID }[]
  >([])
  const [isSelectAllActive, setIsSelectAllActive] = useState(false)

  const classes = useStyles()

  const activeProduct = useActiveProduct()!
  const permissions = activeProduct.viewerPermissions
  const canEmail = permissions.has("emails.manage")
  const canManageGroups = permissions.has("member_groups.manage")
  const canMakeTeacher = permissions.has("invitations.manage")
  const canUnenroll = permissions.has("members.manage")

  const { app } = useLazyLoadQuery<ProductMembersList__membersDirectoryFiltersQuery>(
    graphql`
      query ProductMembersList__membersDirectoryFiltersQuery($id: ID!) {
        app: node(id: $id) {
          ... on ProductApp {
            membersDirectoryFilters {
              edges {
                node {
                  id
                  memberGroupId
                }
              }
            }
          }
        }
      }
    `,
    {
      id: appId || "",
    }
  )

  const membersDirectoryFilters = Relay.connectionToArray(app?.membersDirectoryFilters)
  const membersAppGroupIdsFilter = membersDirectoryFilters
    .filter((maf) => maf.memberGroupId && maf.memberGroupId?.length > 0)
    .map((maf) => maf.memberGroupId!)

  const { node } = useLazyLoadQuery<ProductMembersListQuery>(
    graphql`
      query ProductMembersListQuery(
        $id: ID!
        $canMakeTeacher: Boolean!
        $canUnenroll: Boolean!
        $productRoles: [ProductRole!]
        $memberGroupFilterInput: MemberGroupFilterInput
        $groupIds: [ID!]
        $profileFields: [OrganizationMembershipsProfileFieldFilter!]
        $search: String
        $orderBy: OrderByInput
        $first: Int!
        $after: String
      ) {
        node(id: $id) {
          ... on Product {
            id
            ...ProductMembersList_PaginationFragment
              @arguments(
                canMakeTeacher: $canMakeTeacher
                canUnenroll: $canUnenroll
                productRoles: $productRoles
                memberGroupFilterInput: $memberGroupFilterInput
                profileFields: $profileFields
                groupIds: $groupIds
                search: $search
                orderBy: $orderBy
                first: $first
                after: $after
              )
          }
        }
      }
    `,
    {
      id: activeProduct.id,
      canMakeTeacher,
      canUnenroll,
      productRoles: toolbarState.selectedRole?.value
        ? [toolbarState.selectedRole.value]
        : null,
      first: MEMBERS_PER_PAGE,
      memberGroupFilterInput: mapSelectedGroupFilterToInput(toolbarState),
      profileFields: mapSelectedProfileFieldsToInput(toolbarState),
      search: toolbarState.search,
      groupIds: membersAppGroupIdsFilter,
      orderBy: {
        field: mapOrderByFieldToColumn(toolbarState.sort.order.field),
        direction: toolbarState.direction as OrderDirection,
      },
    },
    { fetchPolicy: "network-only" }
  )

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    ProductMembersListPaginationQuery,
    ProductMembersList_PaginationFragment$key
  >(
    graphql`
      fragment ProductMembersList_PaginationFragment on Product
      @refetchable(queryName: "ProductMembersListPaginationQuery")
      @argumentDefinitions(
        canMakeTeacher: { type: "Boolean!" }
        canUnenroll: { type: "Boolean!" }
        productRoles: { type: "[ProductRole!]" }
        memberGroupFilterInput: { type: "MemberGroupFilterInput" }
        profileFields: { type: "[OrganizationMembershipsProfileFieldFilter!]" }
        groupIds: { type: "[ID!]" }
        search: { type: "String" }
        orderBy: { type: "OrderByInput" }
        first: { type: "Int!" }
        after: { type: "String" }
      ) {
        productMemberships(
          roles: $productRoles
          memberGroupFilterInput: $memberGroupFilterInput
          profileFields: $profileFields
          groupIds: $groupIds
          search: $search
          orderBy: $orderBy
          first: $first
          after: $after
        ) @connection(key: "ProductMembersList__productMemberships") {
          __id
          totalCount
          edges {
            node {
              id
              member {
                id
                firstName
              }
              ...ProductMemberCardFragment
                @arguments(canMakeTeacher: $canMakeTeacher, canUnenroll: $canUnenroll)
            }
            cursor
          }
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    node
  )

  // if experience is updated to be auto-join on ExperienceSettingsForm submit, this connection gets invalidated
  useSubscribeToInvalidationState(
    data?.productMemberships?.__id ? [data.productMemberships.__id] : [],
    refetchMemberships
  )

  // Reset selections when filters get applied
  useEffect(() => {
    handleCloseFloatingBar()
  }, [toolbarState])

  const membersLabel = useLabel("product_member")

  if (!node || !data) return null

  const productMemberships = canMakeTeacher
    ? Relay.connectionToArray(data.productMemberships)
    : Relay.connectionToArray(data.productMemberships).filter((pm) => pm.member.firstName)

  return (
    <div className={classes.gridContainer}>
      <div className={classes.selectContainer}>
        {(canEmail || canManageGroups) &&
          productMemberships.length > 0 &&
          !hideSelectAll && (
            <DiscoCheckbox
              testid={"ProductMembersList.select-all"}
              label={"Select All"}
              onChange={handleSelectAllMembersOnPage}
              checked={
                isSelectAllActive ||
                selectedMemberships.length === productMemberships.length
              }
            />
          )}
      </div>

      {
        // toolbarState.selectedGroup.value === "all" && // Only allow select all members in product if on All Groups tab
        selectedMemberships.length === productMemberships.length &&
          selectedMemberships.length < data.productMemberships.totalCount &&
          productMemberships.length > 0 && (
            <DiscoAlert
              data-testid={"ProductMembersList.total-selected-alert-banner"}
              message={
                isSelectAllActive ? (
                  <div className={classes.selectMoreContainer}>
                    <DiscoText>{`All ${data.productMemberships.totalCount} ${membersLabel.plural} are selected.`}</DiscoText>
                    <DiscoTextButton onClick={handleSelectAllMembersOnPage}>
                      {"Clear selection"}
                    </DiscoTextButton>
                  </div>
                ) : (
                  <div className={classes.selectMoreContainer}>
                    <DiscoText>{`Only ${selectedMemberships.length} ${membersLabel.plural} are selected.`}</DiscoText>
                    <DiscoTextButton
                      onClick={handleSelectAllMembersInProduct}
                    >{`Select all ${data.productMemberships.totalCount} ${membersLabel.plural}`}</DiscoTextButton>
                  </div>
                )
              }
              color={"warning"}
            />
          )
      }

      {productMemberships.length ? (
        <DiscoGrid
          data={productMemberships}
          item={(index, pms) => {
            const pm = pms[index]
            return (
              <ProductMemberCard
                key={pm.id}
                testid={`ProductMemberCard-${index}`}
                className={classes.card}
                isSelected={isMembershipSelected(pm.id)}
                handleSelectMember={handleSelectMember}
                refetchMemberships={refetchMemberships}
                productMembershipKey={pm}
                groovyDepths={groovyDepths}
              />
            )
          }}
          refetch={{
            isLoading: isLoadingNext,
            hasNext,
            loadMore: () => loadNext(MEMBERS_PER_PAGE),
          }}
        />
      ) : (
        <MembersListNoneFound title={`No ${membersLabel.plural} Found`} />
      )}

      <ProductMembersListFloatingBar
        memberships={isSelectAllActive ? undefined : selectedMemberships}
        allSelected={
          isSelectAllActive
            ? {
                isAllSelected: isSelectAllActive,
                totalCount: data.productMemberships.totalCount,
              }
            : undefined
        }
        onClose={handleCloseFloatingBar}
        selectedGroupFilter={
          toolbarState.selectedGroup.value === "all"
            ? undefined
            : toolbarState.selectedGroup.value
        }
      />
    </div>
  )

  function handleCloseFloatingBar() {
    setSelectedMemberships([])
    setIsSelectAllActive(false)
  }

  function handleSelectAllMembersOnPage() {
    if (isSelectAllActive || selectedMemberships.length >= productMemberships.length) {
      handleCloseFloatingBar()
    } else {
      setSelectedMemberships(
        productMemberships.map((pm) => {
          return { id: pm.id, memberId: pm.member.id }
        })
      )
    }
  }

  function handleSelectAllMembersInProduct() {
    setIsSelectAllActive((prev) => !prev)
  }

  function handleSelectMember(productMembershipId: GlobalID, userId: GlobalID) {
    const isMemberSelected = isMembershipSelected(productMembershipId)
    let newSelectedMemberships: {
      id: string
      memberId: string
    }[]

    if (isMemberSelected) {
      // de-select
      if (isSelectAllActive) {
        setIsSelectAllActive(false)
        newSelectedMemberships = productMemberships
          .filter((m) => m.id !== productMembershipId)
          .map((pm) => {
            return { id: pm.id, memberId: pm.member.id }
          })
      } else {
        newSelectedMemberships = selectedMemberships.filter(
          (m) => m.id !== productMembershipId
        )
      }
    } else {
      // select
      newSelectedMemberships = [
        ...selectedMemberships,
        { id: productMembershipId, memberId: userId },
      ]
    }
    setSelectedMemberships(newSelectedMemberships)
  }

  function refetchMemberships() {
    refetch(
      { memberGroupFilterInput: mapSelectedGroupFilterToInput(toolbarState) },
      { fetchPolicy: "network-only" }
    )
  }

  function isMembershipSelected(productMembershipId: GlobalID) {
    if (isSelectAllActive) return true
    return selectedMemberships.map((sm) => sm.id).includes(productMembershipId)
  }
}

const useStyles = makeUseStyles((theme) => ({
  gridContainer: {
    display: "grid",
    gap: theme.spacing(1),
  },
  card: { height: "300px" },
  selectContainer: {
    display: "flex",
    alignItems: "center",
    paddingTop: "0 !important",
  },
  selectMoreContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}))

export default Relay.withSkeleton<ProductMembersListProps>({
  component: ProductMembersList,
  skeleton: ProductMembersListSkeleton,
})
